<script setup>
import { onMounted, onUnmounted } from "vue"
import { useSearchAutocomplete } from "../composables/useSearchAutocomplete"
import { highlight } from "../utils/search_highlight"
import { formatRelativeTime } from "../utils/format_relative_time"

const props = defineProps({
  url: { type: String, required: true },
  recentUrl: { type: String, default: "" },
  adminMode: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  showShortcut: { type: Boolean, default: true },
  labels: {
    type: Object,
    default: () => ({}),
  },
  hostElement: { type: Object, default: null },
})

const {
  query,
  recentSearches,
  recentItems,
  selectedIndex,
  isLoading,
  isLoadingRecent,
  errorMessage,
  showEmptyState,
  showDropdown,
  showEmpty,
  label,
  scheduleSearch,
  onFocus,
  onBlur,
  onKeydown,
  selectItem,
  handleItemClick,
  useRecentSearch,
  groupedEntries,
  itemDomId,
  resolveGlobalItemIndex,
  recentItemIndex,
  recentSearchIndex,
  cleanup,
} = useSearchAutocomplete(props)

function onItemMouseDown(event) {
  event.preventDefault()
}

function handleGlobalKeydown(event) {
  if (
    event.key === "/" &&
    !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement.tagName) &&
    !document.activeElement.isContentEditable &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.altKey
  ) {
    event.preventDefault()
    const input = document.querySelector(".search-autocomplete input")
    input?.focus()
    input?.select()
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener("keydown", handleGlobalKeydown)
  cleanup()
})
</script>

<template>
  <div class="search-autocomplete-root">
    <div class="search-input-wrapper">
      <i class="fa-solid fa-magnifying-glass search-icon" />
      <input
        v-model="query"
        type="text"
        :placeholder="placeholder"
        autocomplete="off"
        spellcheck="false"
        @input="scheduleSearch"
        @keydown="onKeydown"
        @focus="onFocus"
        @blur="onBlur"
      >
      <kbd
        v-if="showShortcut"
        class="search-shortcut"
        data-bs-toggle="tooltip"
        :data-bs-title="label('shortcutHint', 'Press / to focus search')"
      >/</kbd>
    </div>

    <div
      v-show="showDropdown"
      class="search-results"
      role="listbox"
      :aria-label="label('resultsLabel', 'Search results')"
    >
      <div v-if="isLoading || isLoadingRecent" class="search-loading">
        {{ label("searching", "Searching...") }}
      </div>

      <div v-else-if="errorMessage" class="search-error">
        {{ errorMessage }}
      </div>

      <template v-else-if="showEmptyState">
        <div v-if="recentItems.length > 0" class="search-group">
          <div class="search-group-label">
            {{ label("recentItems", "Recently Viewed") }}
          </div>
          <a
            v-for="(item, itemIndex) in recentItems"
            :id="itemDomId(recentItemIndex(itemIndex))"
            :key="`recent-${item.id}`"
            :href="adminMode ? '#' : item.url"
            class="search-item search-recent-item"
            :class="{ selected: selectedIndex === recentItemIndex(itemIndex) }"
            @mousedown="onItemMouseDown"
            @mouseenter="selectItem(recentItemIndex(itemIndex))"
            @click.prevent="handleItemClick(item)"
          >
            <i :class="['fa-solid', item.icon || 'fa-file', 'search-item-icon']" />
            <div class="search-item-content">
              <span class="search-item-name">{{ item.name }}</span>
              <span v-if="item.preview" class="search-item-preview">{{ item.preview }}</span>
            </div>
            <div class="search-item-badges">
              <span
                v-if="item.status_label"
                class="badge badge-status"
                :class="`badge-status-${item.status}`"
              >{{ item.status_label }}</span>
              <span class="badge badge-type">{{ item.type_label || item.type }}</span>
              <span v-if="item.book" class="badge badge-book">{{ item.book }}</span>
              <span v-if="item.viewed_at" class="badge badge-time">{{ formatRelativeTime(item.viewed_at) }}</span>
            </div>
          </a>
        </div>

        <div v-if="recentSearches.length > 0" class="search-group">
          <div class="search-group-label">
            {{ label("recent", "Recent Searches") }}
          </div>
          <div
            v-for="(term, termIndex) in recentSearches"
            :id="itemDomId(recentSearchIndex(termIndex))"
            :key="`term-${term}`"
            class="search-item search-recent"
            :class="{ selected: selectedIndex === recentSearchIndex(termIndex) }"
            @mousedown="onItemMouseDown"
            @mouseenter="selectItem(recentSearchIndex(termIndex))"
            @click="useRecentSearch(term)"
          >
            <i class="fa-solid fa-clock-rotate-left search-item-icon" />
            <span class="search-item-name">{{ term }}</span>
          </div>
        </div>

        <div class="search-hint">
          {{ label("startTyping", "Start typing to search...") }}
        </div>
      </template>

      <div v-else-if="showEmpty" class="search-empty">
        {{ label("noResults", "No results").replace("%{query}", query.trim()) }}
      </div>

      <template v-else>
        <div
          v-for="([type, items], groupIndex) in groupedEntries()"
          :key="type"
          class="search-group"
        >
          <div class="search-group-label">
            {{ items[0].type_label || type }}
          </div>
          <a
            v-for="(item, itemIndex) in items"
            :id="itemDomId(resolveGlobalItemIndex(groupIndex, itemIndex))"
            :key="item.id"
            :href="adminMode ? '#' : item.url"
            class="search-item"
            :class="{ selected: selectedIndex === resolveGlobalItemIndex(groupIndex, itemIndex) }"
            @mousedown="onItemMouseDown"
            @mouseenter="selectItem(resolveGlobalItemIndex(groupIndex, itemIndex))"
            @click.prevent="handleItemClick(item)"
          >
            <i :class="['fa-solid', item.icon || 'fa-file', 'search-item-icon']" />
            <div class="search-item-content">
              <span class="search-item-name" v-html="highlight(item.name, query.trim())" />
              <span v-if="item.preview" class="search-item-preview">{{ item.preview }}</span>
            </div>
            <div class="search-item-badges">
              <span
                v-if="item.status_label"
                class="badge badge-status"
                :class="`badge-status-${item.status}`"
              >{{ item.status_label }}</span>
              <span class="badge badge-type">{{ item.type_label || item.type }}</span>
              <span v-if="item.book" class="badge badge-book">{{ item.book }}</span>
            </div>
          </a>
        </div>
      </template>
    </div>
  </div>
</template>
